import { useMemo } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { Button } from "@aix/ui"

import { trpc } from "../../lib/trpc"
import { RunLogDrawer } from "./RunLogDrawer"

interface Run {
  id: number
  agent_id: number
  status: "queued" | "running" | "completed" | "failed"
  prompt?: string | null
  pr_url?: string | null
  error?: string | null
  triggered_by?: string | null
  started_at: string
  ended_at?: string | null
}

interface Agent {
  id: number
  name: string
}

interface RunsHistorySearch {
  run?: number | string
  agent?: number | string
}

const STATUS_DOT: Record<Run["status"], string> = {
  completed: "bg-green-500",
  running: "bg-blue-500 animate-pulse",
  queued: "bg-muted-foreground",
  failed: "bg-red-500",
}

function fmtAge(iso?: string | null): string {
  if (!iso) return "never"
  const sec = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (sec < 60) return `${sec}s ago`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function fmtDuration(start?: string | null, end?: string | null): string {
  if (!start) return ""
  const s = new Date(start).getTime()
  const e = end ? new Date(end).getTime() : Date.now()
  const sec = Math.max(0, Math.round((e - s) / 1000))
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}m ${sec % 60}s`
  return `${Math.floor(m / 60)}h ${m % 60}m`
}

export default function AgentsRunsHistoryScreen() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as RunsHistorySearch
  const selectedAgentId = coerceNumberParam(search.agent)
  const selectedRunId = coerceNumberParam(search.run)

  const setSearch = (next: Partial<RunsHistorySearch>) =>
    navigate({
      to: "/agents/runs",
      search: (prev: Record<string, unknown>) => {
        const merged = { ...(prev as RunsHistorySearch), ...next }
        ;(Object.keys(merged) as (keyof RunsHistorySearch)[]).forEach((k) => {
          if (merged[k] === undefined) delete merged[k]
        })
        return merged
      },
    })

  const jobsQuery = trpc.brainAgents.jobs.useQuery(
    { agentId: selectedAgentId ?? undefined, limit: 200 },
    {
      refetchInterval: (data: { runs?: Run[] } | undefined) => {
        const hasLive = (data?.runs ?? []).some((r) => r.status === "queued" || r.status === "running")
        return hasLive ? 5000 : 30000
      },
    },
  )
  const agentsQuery = trpc.brainAgents.list.useQuery()

  const jobsResponse = jobsQuery.data as unknown as { runs?: Run[] } | undefined
  const agentsResponse = agentsQuery.data as unknown as { agents?: Agent[] } | undefined
  const runs = jobsResponse?.runs ?? []
  const agents = useMemo(() => agentsResponse?.agents ?? [], [agentsResponse?.agents])
  const nameOf = useMemo(() => {
    const m = new Map<number, string>()
    agents.forEach((a) => m.set(a.id, a.name))
    return (id: number) => m.get(id) || `agent#${id}`
  }, [agents])

  return (
    <div className="h-full flex flex-col bg-background">
      <header className="border-b-4 border-border bg-secondary p-4 md:p-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">📜 Agent runs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every run across every agent. Click a row for full output.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedAgentId ?? ""}
            onChange={(e) => setSearch({ agent: e.target.value ? Number(e.target.value) : undefined })}
            className="border-2 border-border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <Button variant="outline" onClick={() => navigate({ to: "/agents" })}>
            ← Agents
          </Button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-6">
        {jobsQuery.isLoading && (
          <div className="bg-background border-2 border-border p-8 text-center text-muted-foreground">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2" />
            <div>Loading runs…</div>
          </div>
        )}

        {!jobsQuery.isLoading && runs.length === 0 && <div className="text-sm text-muted-foreground">No runs yet.</div>}

        <div className="space-y-1 max-w-5xl">
          {runs.map((r) => (
            <button
              key={r.id}
              onClick={() => setSearch({ run: r.id })}
              className="w-full text-left border-2 border-border p-2 flex items-center justify-between gap-2 text-sm hover:bg-secondary transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[r.status]}`} />
                <span className="text-xs font-bold uppercase text-muted-foreground w-20 shrink-0">{r.status}</span>
                <span className="text-xs text-foreground font-bold w-44 shrink-0 truncate">{nameOf(r.agent_id)}</span>
                <span className="text-muted-foreground truncate">{r.prompt || "—"}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                {r.triggered_by && <span className="hidden md:inline">{r.triggered_by}</span>}
                <span className="font-mono">⏱ {fmtDuration(r.started_at, r.ended_at)}</span>
                {r.pr_url && (
                  <a
                    href={r.pr_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-foreground underline"
                  >
                    PR
                  </a>
                )}
                <span title={new Date(r.started_at).toLocaleString()}>{fmtAge(r.started_at)}</span>
              </div>
            </button>
          ))}
        </div>
      </main>

      {selectedRunId != null && <RunLogDrawer runId={selectedRunId} onClose={() => setSearch({ run: undefined })} />}
    </div>
  )
}

function coerceNumberParam(value: number | string | undefined): number | null {
  if (value == null) return null
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}
